import React, { useState, useRef, useEffect } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import './ResizableImage.css';

interface ImageAttrs {
  src: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  float?: 'left' | 'right' | 'none';
}

const ResizableImageComponent: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
}) => {
  const attrs = node.attrs as ImageAttrs;
  const { src, alt, title, width: initialWidth, height: initialHeight, float = 'none' } = attrs;
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({
    width: initialWidth || 300,
    height: initialHeight || 200,
  });
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const startPos = useRef({ x: 0, y: 0 });
  const startDims = useRef({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const aspectRatio = useRef(1);

  // Load natural dimensions when image loads
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      const img = imageRef.current;
      setNaturalDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      aspectRatio.current = img.naturalWidth / img.naturalHeight;

      // If no initial dimensions, set to natural or max 600px width
      if (!initialWidth && !initialHeight) {
        const maxWidth = 600;
        if (img.naturalWidth > maxWidth) {
          const newWidth = maxWidth;
          const newHeight = maxWidth / aspectRatio.current;
          setDimensions({ width: newWidth, height: newHeight });
          updateAttributes({ width: newWidth, height: newHeight });
        } else {
          setDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          updateAttributes({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }
      }
    }
  }, [src, initialWidth, initialHeight, updateAttributes]);

  // Cap dimensions when float changes to left/right
  useEffect(() => {
    const maxWidth = 420;
    if ((float === 'left' || float === 'right') && dimensions.width > maxWidth) {
      const newWidth = maxWidth;
      const newHeight = newWidth / aspectRatio.current;
      setDimensions({ width: newWidth, height: newHeight });
      updateAttributes({ width: Math.round(newWidth), height: Math.round(newHeight) });
    }
  }, [float]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    startPos.current = { x: e.clientX, y: e.clientY };
    startDims.current = { ...dimensions };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeHandle) return;

      const deltaX = e.clientX - startPos.current.x;
      const deltaY = e.clientY - startPos.current.y;

      let newWidth = startDims.current.width;
      let newHeight = startDims.current.height;

      // Max width for floated images to leave room for text wrapping
      const maxWidth = (float === 'left' || float === 'right') ? 420 : Infinity;

      switch (resizeHandle) {
        case 'se': // Southeast (bottom-right)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width + deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'sw': // Southwest (bottom-left)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width - deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'ne': // Northeast (top-right)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width + deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'nw': // Northwest (top-left)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width - deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'e': // East (right)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width + deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'w': // West (left)
          newWidth = Math.min(maxWidth, Math.max(50, startDims.current.width - deltaX));
          newHeight = newWidth / aspectRatio.current;
          break;
        case 'n': // North (top)
          newHeight = Math.max(50, startDims.current.height - deltaY);
          newWidth = newHeight * aspectRatio.current;
          // Cap width for floated images
          if (newWidth > maxWidth) {
            newWidth = maxWidth;
            newHeight = newWidth / aspectRatio.current;
          }
          break;
        case 's': // South (bottom)
          newHeight = Math.max(50, startDims.current.height + deltaY);
          newWidth = newHeight * aspectRatio.current;
          // Cap width for floated images
          if (newWidth > maxWidth) {
            newWidth = maxWidth;
            newHeight = newWidth / aspectRatio.current;
          }
          break;
      }

      setDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        setResizeHandle(null);
        // Update the node attributes with final dimensions
        updateAttributes({
          width: Math.round(dimensions.width),
          height: Math.round(dimensions.height),
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeHandle, dimensions, updateAttributes]);

  return (
    <NodeViewWrapper
      className={`resizable-image-wrapper ${selected ? 'selected' : ''} ${isResizing ? 'resizing' : ''} float-${float}`}
      data-drag-handle
      data-float={float}
    >
      <div
        className="resizable-image-container"
        style={{
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          float: float !== 'none' ? float : undefined,
          margin: float === 'left' ? '0 16px 16px 0' : float === 'right' ? '0 0 16px 16px' : '0 auto',
        }}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt || ''}
          title={title || ''}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: isResizing ? 'none' : 'auto',
          }}
          onLoad={() => {
            if (imageRef.current) {
              const img = imageRef.current;
              setNaturalDimensions({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
              aspectRatio.current = img.naturalWidth / img.naturalHeight;
            }
          }}
        />

        {/* Resize Handles - only show when selected */}
        {selected && (
          <>
            <div
              className="resize-handle resize-handle-nw"
              onMouseDown={(e) => handleMouseDown(e, 'nw')}
            />
            <div
              className="resize-handle resize-handle-ne"
              onMouseDown={(e) => handleMouseDown(e, 'ne')}
            />
            <div
              className="resize-handle resize-handle-sw"
              onMouseDown={(e) => handleMouseDown(e, 'sw')}
            />
            <div
              className="resize-handle resize-handle-se"
              onMouseDown={(e) => handleMouseDown(e, 'se')}
            />
            <div
              className="resize-handle resize-handle-n"
              onMouseDown={(e) => handleMouseDown(e, 'n')}
            />
            <div
              className="resize-handle resize-handle-s"
              onMouseDown={(e) => handleMouseDown(e, 's')}
            />
            <div
              className="resize-handle resize-handle-e"
              onMouseDown={(e) => handleMouseDown(e, 'e')}
            />
            <div
              className="resize-handle resize-handle-w"
              onMouseDown={(e) => handleMouseDown(e, 'w')}
            />

            {/* Dimension display */}
            <div className="resize-dimensions">
              {Math.round(dimensions.width)} × {Math.round(dimensions.height)}
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default ResizableImageComponent;
